import {
  Box,
  MenuItem,
  Select,
  type SelectProps,
  useTheme,
} from "@mui/material";
import { ChevronDown, ChevronUp } from "lucide-react";
import { forwardRef, type Key, useState } from "react";

type QuestTypePair = {
  key: Key | null | undefined;
  value: string;
};

export interface SelectInputProps
  extends Omit<SelectProps, "onChange" | "value"> {
  value: string;
  onChange: (value: string) => void;
  options: QuestTypePair[];
  id?: string;
  error?: boolean;
}

export const SelectInput = forwardRef<HTMLSelectElement, SelectInputProps>(
  ({ value, onChange, options, id, sx, error, ...props }, ref) => {
    const { colors, customSpacing, radius, typography } = useTheme();
    const [isOpen, setIsOpen] = useState(false);

    return (
      <Select
        ref={ref}
        value={value}
        onChange={(e) => onChange(e.target.value as string)}
        id={id}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        IconComponent={() => (
          <Box
            style={{
              position: "absolute",
              right: customSpacing.md,
              top: "50%",
              transform: "translateY(-50%)",
              pointerEvents: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isOpen ? (
              <ChevronUp size={20} style={{ color: colors.text.secondary }} />
            ) : (
              <ChevronDown size={20} style={{ color: colors.text.secondary }} />
            )}
          </Box>
        )}
        sx={{
          width: "100%",
          minHeight: 41.6,
          fontFamily: typography.fontFamily,
          fontSize: typography.body1.fontSize,
          borderRadius: radius.md,
          border: `1px solid ${colors.border.primary}`,
          boxSizing: "border-box",
          position: "relative",
          "& .MuiSelect-icon": {
            display: "none",
          },
          "& .MuiSelect-iconOpen": {
            display: "none",
          },
          "& .MuiSelect-select": {
            padding: `${customSpacing.xs} ${customSpacing.md}`,
            paddingRight: `calc(${customSpacing.md} + 20px + ${customSpacing.sm})`,
            background: colors.background.primary,
            color: colors.text.primary,
            transition: "border-color 0.2s, box-shadow 0.2s",
            height: "100%",
            display: "flex",
            alignItems: "center",
            "&:focus": {
              outline: "none",
            },
          },
          "& .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
          ...sx,
        }}
        {...props}
      >
        {options.map((option) => (
          <MenuItem
            key={option.key}
            value={String(option.key)}
            sx={{
              fontFamily: typography.fontFamily,
              fontSize: typography.body1.fontSize,
              color: colors.text.primary,
              backgroundColor: colors.background.primary,
              "&:hover": {
                backgroundColor: colors.background.secondary,
              },
              "&.Mui-selected": {
                backgroundColor: colors.background.secondary,
                "&:hover": {
                  backgroundColor: colors.background.secondary,
                },
              },
            }}
          >
            {option.value}
          </MenuItem>
        ))}
      </Select>
    );
  },
);
